import { useFileRecord } from "@/api/fileRecords";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  useResolvedFileRecordActions,
} from "@/object-actions";
import { resolveFileRecordShowPublish } from "@/panes/file-record-detail/publish";
import { useFileRecordPaneHeaderKebabHandlerDeps } from "@/panes/file-record-detail/useFileRecordPaneHeaderKebabHandlerDeps";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { MoreHorizontal } from "lucide-react";

/**
 * Wraps a chat file-record entity card (message attachment or inline
 * reference) with the SAME tier-aware action set the file detail pane's
 * kebab resolves — one menu, two triggers: a hover-revealed kebab on the
 * card's corner and right-click anywhere on it. Container files offer Add
 * to Library; references get their reduced set; Library files the full set
 * (the object-actions manifest decides — nothing chat-specific here).
 *
 * The record fetches lazily on first open (cards hydrate entities, not
 * records), so a chat full of cards costs nothing until a menu is asked for.
 */
export function ChatFileRecordCardShell({
  fileRecordId,
  name,
  className,
  children,
}: {
  fileRecordId: string;
  /** Display name for the menu's accessible labels. */
  name?: string;
  className?: string;
  /** Render prop — pass `menuSlot` into the card's own footer slot
   *  (`EntityCard menuSlot={menuSlot}`), so the kebab sits in the footer
   *  row beside the filename, not overlaid on the thumbnail. */
  children: (slots: { menuSlot: React.ReactNode }) => React.ReactNode;
}) {
  const menu = useRailRowContextMenu();
  const { data: record } = useFileRecord(fileRecordId, {
    enabled: menu.open && !!fileRecordId,
  });

  const handlerDeps = useFileRecordPaneHeaderKebabHandlerDeps(
    record ?? ({ id: "", path: "" } as FileRecord),
  );

  const { actions, kind } = useResolvedFileRecordActions({
    record: record ?? null,
    surface: "left-rail",
    enabled: menu.open,
    state: record
      ? {
          canShare: record.can_edit !== false,
          canEdit: record.can_edit !== false,
          canDelete: record.can_manage !== false,
          canMove: record.can_manage !== false,
          showPublish: resolveFileRecordShowPublish({
            record,
            canEdit: record.can_edit !== false,
          }),
        }
      : {},
    handlerDeps,
  });

  // A4: a Library file surfaced in a chat is a reference APPEARANCE — the
  // card stands for "this file, referenced here", not for the file at home.
  // Its menu keeps every verb that acts on the real file THROUGH the
  // reference (Open / Download / star / Share / Publish / Copy URL — share
  // and publish resolve to the target server-side, so publishing here never
  // mints a second publish), and drops the verbs that relocate or retire
  // the file (Move to, Archive — those stay on Library surfaces). A sent
  // message's attachment has no detach verb at all. Container files
  // uploaded to this chat keep their full container set (Add to Library,
  // Archive) — those live here.
  const isReferenceAppearance =
    !!record && (record.kind === "link" || record.class === "workspace");
  const visibleActions = isReferenceAppearance
    ? actions.filter((a) =>
        [
          "open",
          "download",
          "star",
          "unstar",
          "share",
          "publish",
          "copy-url",
        ].includes(a.id),
      )
    : actions;

  const label =
    name || record?.path?.split("/").filter(Boolean).pop() || "File";

  // The footer kebab — lives in the card's own menuSlot (beside the
  // filename), hover-revealed like every row kebab. toggleFromButton anchors
  // the menu just below the button.
  const menuSlot = (
    <button
      type="button"
      aria-label={`${label} actions`}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        menu.toggleFromButton(e);
      }}
      className={cn(
        "inline-flex size-6 items-center justify-center rounded-sm text-n-50 outline-none transition-[opacity,color,background-color] duration-150 ease-out hover:bg-n-10 hover:text-n-90 hover:duration-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
        menu.open
          ? "opacity-100"
          : "opacity-0 group-hover/row:opacity-100 focus-within:opacity-100",
      )}
    >
      <MoreHorizontal className="size-4" />
    </button>
  );

  return (
    <div
      className={cn("group/row relative", className)}
      onContextMenu={menu.onContextMenu}
    >
      {children({ menuSlot })}
      <RailRowMenu
        {...railRowMenuProps(menu)}
        label={label}
        kebab={false}
        contentWidthClassName="w-52"
      >
        {record && kind ? (
          <ObjectActionMenu actions={visibleActions} kind={kind} name={label} />
        ) : (
          <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
        )}
      </RailRowMenu>
    </div>
  );
}
